import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import LinearProgress from "@mui/material/LinearProgress"
import Pagination from "@mui/material/Pagination"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useSnackbar } from "notistack"
import { getEventRegistrations } from "services/event"
import { verifyResponse } from "utils/httpRequest"
import { formatEventDateForDisplay } from "utils/time"

const PAGE_SIZE = 25

const getParticipantName = (participant) =>
  [participant.firstName, participant.lastName].filter(Boolean).join(" ") ||
  "Unnamed member"

const getStatusColor = (status) => {
  if (status === "attended") return "success"
  if (status === "no_show") return "warning"
  return "primary"
}

const EventParticipantsDialog = ({ open, event, onClose }) => {
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => clearTimeout(timer)
  }, [search])

  const loadParticipants = useCallback(async () => {
    if (!open || !event?.id) return

    setLoading(true)
    try {
      const response = await getEventRegistrations({
        eventId: event.id,
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
      })
      verifyResponse(response.data)
      const payload = response?.data?.data ?? response?.data ?? {}
      setParticipants(payload.registrations || [])
      setTotal(payload.total || 0)
    } catch (error) {
      setParticipants([])
      setTotal(0)
      enqueueSnackbar("Failed to load event participants", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, enqueueSnackbar, event?.id, open, page])

  useEffect(() => {
    loadParticipants()
  }, [loadParticipants])

  useEffect(() => {
    if (!open) {
      setParticipants([])
      setTotal(0)
      setPage(1)
      setSearch("")
      setDebouncedSearch("")
    }
  }, [open])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="h2" variant="h6" fontWeight={800}>
          Registered Participants
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {event?.title || "Event"} · {total} participant{total === 1 ? "" : "s"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          label="Search name or email"
          size="small"
          fullWidth
          sx={{ mb: 2, mt: 0.5 }}
        />
        {loading && <LinearProgress />}
        <TableContainer
          sx={{
            maxHeight: 480,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Attendance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && participants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No registered participants found.
                  </TableCell>
                </TableRow>
              )}
              {participants.map((participant) => (
                <TableRow key={participant.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {getParticipantName(participant)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {participant.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={String(participant.registrationStatus || "registered").replace("_", " ")}
                      color={getStatusColor(participant.registrationStatus)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {formatEventDateForDisplay(participant.registrationDate, event?.timezone)}
                  </TableCell>
                  <TableCell>
                    {participant.attendanceConfirmed
                      ? formatEventDateForDisplay(participant.attendanceDate, event?.timezone) || "Confirmed"
                      : "Not confirmed"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default EventParticipantsDialog
